import React, { useCallback } from 'react';

import Tooltip from '@mui/material/Tooltip';

import DeckOptionsPanel, {
  DECK_OPTIONS_PANEL_ID,
} from 'client/app/components/Parameters/DeckOptions/DeckOptionsPanel';
import { useConfiguredDevicesContext } from 'client/app/state/ConfiguredDevicesProvider/ConfiguredDevicesProvider';
import { ConfiguredDevice } from 'common/types/bundle';
import Button from 'common/ui/components/Button';
import { DeckOptionsIcon } from 'common/ui/icons';
import { useAdditionalPanelContext } from 'common/ui/providers/AdditionalPanelProvider';

type Props = {
  value: ConfiguredDevice;
  onChange: (configuredDevice: ConfiguredDevice) => void;
  isDisabled: boolean;
};

/**
 * This component launches the DeckOptionsPanel.
 */
export default function DeckOptionsParameter({
  value: configuredDevice,
  onChange,
  isDisabled,
}: Props) {
  const {
    setActiveConfiguredDeviceIds,
    activeMainConfiguredDevice,
    availableMainDeviceConfigOptions,
  } = useConfiguredDevicesContext();
  const { setAdditionalPanel, clearAdditionalPanel, additionalPanelId } =
    useAdditionalPanelContext();

  const isDeckOptionsPanelOpen = additionalPanelId === DECK_OPTIONS_PANEL_ID;
  const handleToggleDeckOptionsPanel = useCallback(() => {
    if (isDeckOptionsPanelOpen) {
      clearAdditionalPanel();
    } else {
      setActiveConfiguredDeviceIds([configuredDevice.id]);
      setAdditionalPanel({
        id: DECK_OPTIONS_PANEL_ID,
        contents: (
          <DeckOptionsPanel
            onChange={onChange}
            onClose={clearAdditionalPanel}
            isDisabled={isDisabled}
          />
        ),
      });
    }
  }, [
    isDeckOptionsPanelOpen,
    clearAdditionalPanel,
    setActiveConfiguredDeviceIds,
    configuredDevice.id,
    setAdditionalPanel,
    onChange,
    isDisabled,
  ]);

  const configOption = availableMainDeviceConfigOptions.find(
    option => option.value === activeMainConfiguredDevice?.runConfigId,
  );
  const isGilson = activeMainConfiguredDevice?.type === 'GilsonPipetMax';
  const isOptionSelected = configOption !== undefined;
  const tooltipText = isGilson // gilson has no DeviceConfigOption just a default one
    ? 'Default deck selected'
    : isOptionSelected
    ? `Deck "${configOption.label}" selected`
    : '';

  return (
    <Tooltip arrow title={tooltipText}>
      <Button
        sx={{ width: '100%' }}
        color="primary"
        variant="tertiary"
        onClick={handleToggleDeckOptionsPanel}
        size="small"
        startIcon={<DeckOptionsIcon />}
      >
        {isOptionSelected || isGilson ? 'Change' : 'Select'} Deck Layout
      </Button>
    </Tooltip>
  );
}
